import { Link } from 'react-router-dom';
import { ChevronRight, Folder as FolderIcon } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '@/lib/cn';
import type { Folder } from '@/types/api';

export function FolderBreadcrumb({
  orgId,
  folders,
  current,
}: {
  orgId: string;
  folders: Folder[];
  current: Folder | null;
}) {
  const chain: Folder[] = [];
  let cursor = current;
  const byId = new Map(folders.map((f) => [f.id, f]));
  while (cursor) {
    chain.unshift(cursor);
    cursor = cursor.parent_id ? byId.get(cursor.parent_id) ?? null : null;
  }

  return (
    <nav className="flex items-center gap-1 text-sm text-slate-600 flex-wrap">
      <Link
        to={`/workspace/${orgId}`}
        className="inline-flex items-center gap-1 hover:text-bb-navy"
      >
        <FolderIcon size={14} /> Workspace
      </Link>
      {chain.map((f) => (
        <BreadcrumbItem key={f.id} orgId={orgId} folder={f} />
      ))}
    </nav>
  );
}

function BreadcrumbItem({ orgId, folder }: { orgId: string; folder: Folder }) {
  // Each crumb is a file drop target — drop a file row here to move it
  // into that ancestor folder.
  const { setNodeRef, isOver } = useDroppable({
    id: `folder-file-target:${folder.id}`,
    data: { type: 'folder-file-target', folderId: folder.id },
  });

  return (
    <span ref={setNodeRef} className="inline-flex items-center gap-1">
      <ChevronRight size={14} className="text-slate-300" />
      <Link
        to={`/workspace/${orgId}/folders/${folder.id}`}
        className={cn(
          'hover:text-bb-navy px-1',
          isOver && 'border border-dashed border-bb-navy text-bb-navy',
        )}
      >
        {folder.name}
      </Link>
    </span>
  );
}
